import { FC, useState } from 'react';

import { DesktopDatePicker as MuiDesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ReactComponent as ArrowDownIcon } from 'assett/icons/arrow_down_icon.svg';
import { ReactComponent as ArrowlefttIcon } from 'assett/icons/arrow_left_datepicker_icon.svg';
import { ReactComponent as ArrowRightIcon } from 'assett/icons/arrow_right_datepicker_icon.svg';
import { ReactComponent as ArrowUpIcon } from 'assett/icons/arrow_up_icon.svg';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme } from 'stylesHooks';
import { OptionsDatepicker } from 'types/Form';
import { TRANSLATION_KEYS } from 'utils/constants';
import { getDateFormat, getLanguageStorage } from 'utils/localStorage';
import { useTranslation } from 'utils/translations';
import { dayjsForMaterial } from 'utils/utils';

type Props = {
	name?: string;
	value: dayjs.Dayjs | null;
	onChange: (value: dayjs.Dayjs | null) => void;
	label: string;
	variant: keyof typeof OptionsDatepicker;
	minDate?: dayjs.Dayjs | undefined;
	maxDate?: dayjs.Dayjs | undefined;
	required?: boolean;
	readonly?: boolean;
};

const DatePicker: FC<Props> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const {
		name,
		value,
		onChange,
		label,
		variant,
		maxDate: maxDateNF,
		minDate: mindateNF,
		required,
		readonly,
	} = props;

	const minDate = dayjsForMaterial(mindateNF);
	const maxDate = dayjsForMaterial(maxDateNF);

	const [open, setOpen] = useState<boolean>(false);
	const theme = useTheme();

	const [invalidDate, setInvalidDate] = useState(false);

	theme.datepicker = OptionsDatepicker[props.variant as OptionsDatepicker];

	return (
		<MuiDesktopDatePicker
			{...props}
			key={`datepicker_${variant}`}
			maxDate={maxDate}
			minDate={minDate}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			format={getDateFormat()}
			slots={{
				openPickerIcon: () => (!open ? <ArrowDownIcon /> : <ArrowUpIcon />),
				leftArrowIcon: () => <ArrowlefttIcon />,
				rightArrowIcon: () => <ArrowRightIcon />,
			}}
			label={label}
			value={value}
			onChange={e => {
				const newDate = dayjsForMaterial((e as Dayjs).utc(true));
				const maxDateCmp = dayjsForMaterial(maxDate?.utc(true));
				const minDateCmp = dayjsForMaterial(minDate?.utc(true));
				const isValidDate =
					!newDate ||
					(typeof newDate?.isValid() !== 'undefined' &&
						newDate.isValid() &&
						(!maxDateCmp || !newDate.isAfter(maxDateCmp, 'day')) &&
						(!minDateCmp || !newDate.isBefore(minDateCmp, 'day')));
				onChange(isValidDate && newDate ? newDate : null);
				setOpen(false);
				if (isValidDate || value) {
					setInvalidDate(false);
				} else setInvalidDate(true);
			}}
			showDaysOutsideCurrentMonth
			slotProps={{
				textField: {
					fullWidth: true,
					required,
					variant: 'filled' as const,
					autoComplete: 'off',
					sx: open
						? {
								border: `1px solid ${theme.palette.primary.main}`,
								'& .MuiInputBase-root': {
									border: 'none',
								},
							}
						: {},
					InputLabelProps: {
						style: { transform: 'translate(12px 18px)' },
					},
					name,
					error: invalidDate,
					inputProps: {
						readOnly: readonly,
						placeholder:
							getLanguageStorage() !== 'en-us'
								? tClientLabels('commons.ddmmyyyy')
								: tClientLabels('commons.mmddyyyy'),
					},
				},
				popper: {
					style: {
						background: 'transparent',
						transform: 'translate(50px, 10px)',
					},
				},
				desktopPaper: {
					elevation: 0,
					sx: {
						transform: {
							sm: 'translate(0px, 5px)!important',
							lg: 'translate(0px, 5px)!important',
						},
						'& .MuiPickersDay-root:not(.Mui-selected)': {
							border: '0!important',
						},
					},
				},
			}}
		/>
	);
};

export default DatePicker;
